import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from "@angular/core";
import {
    MatMenuTrigger, MatMenu, MatMenuItem
} from "@angular/material/menu";

import { IAction } from "../../interfaces/action.interface";
import { IActionSelectedFile } from "../../interfaces/action-selected-file.interface";
import { FileSelectorComponent } from "../../../components/file-selector/file-selector.component";
import { MimeType } from "@shopliftr/common-js/data";
import { ISelectedFile } from "../../../components/file-selector/interface/selected-file";

import { MatMiniFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { ButtonDirective } from "../../../application/directives/button/button.directive";

/**
 * Provides one or more buttons to be rendered on a list screen
 *
 * If more than one button is available the first in the list will show
 * and the others will appear when the user hovers over the button.
 *
 * See IAction for specific action settings
 */
@Component({
    selector: "mgd-action-button-group",
    templateUrl: "./action-button-group.component.html",
    styleUrls: ["./action-button-group.component.scss"],
    imports: [
        FileSelectorComponent,
        MatMiniFabButton,
        MatTooltip,
        MatIcon,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        ButtonDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionButtonGroupComponent implements OnChanges {
    /**
     * The actions displayed in this group
     */
    @Input() actions: Array<IAction>;

    /**
     * Disable the button group
     */
    @Input() disabled: boolean;

    /**
     * Emits the action id when a non-file action is triggered.
     * File Actions will not emit this event.
     */
    @Output() actionSelect: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Emits when a file is selected after a file-type action
     *
     * @type {EventEmitter<IActionSelectedFile>}
     * @memberof ActionButtonGroupComponent
     */
    @Output() fileSelect: EventEmitter<IActionSelectedFile>
        = new EventEmitter<IActionSelectedFile>();

    @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

    @ViewChild(FileSelectorComponent) fileSelector: FileSelectorComponent;

    acceptedFileTypes?: Array<MimeType>;

    fileAction?: string;

    displayActions: Array<IAction>;

    triggerIcon: string;

    get lastIconDisabled(): boolean {
        return Boolean(
            this.displayActions
            && this.displayActions[this.displayActions.length - 1].disabled
        );
    }

    get includeFileSelector(): boolean {
        return Boolean(
            this.actions?.find(
                (action) => action.fileSelector && !action.disabled
            )
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.actions) {
            if (this.actions?.length > 0) {
                this.displayActions = Array.from(this.actions).reverse();
                this.triggerIcon = this.getIcon(this.actions[0]);
            }
            else {
                this.displayActions = [];
                this.triggerIcon = "more_vert";
            }
        }
    }

    onActionClick(event: MouseEvent, action: IAction): void {
        event.stopPropagation();
        if (action.fileSelector) {
            this.fileAction = action.id;
            this.acceptedFileTypes = action.acceptedFileTypes;
            this.fileSelector.openFileSelectorDialog();
        }
        else {
            this.actionSelect.next(action.id);
        }
    }

    /**
     * Get the icon for the provided action
     */
    getIcon(action: IAction): string {
        let icon: string;

        if (action.toggleValue !== undefined) {
            icon = action.toggleValue ? "check_box" : "check_box_outline_blank";
        }
        else {
            icon = action.icon;
        }

        return icon;
    }

    /**
     * This is the handler for the FileSelectorComponent fileSelect event
     *
     * It tags the selected file with the action and emits a fileSelect event
     */
    onFileSelectorSelected(selectedFile: ISelectedFile): void {
        if (selectedFile) {
            const actionFile = selectedFile as IActionSelectedFile;
            actionFile.actionId = this.fileAction;
            this.fileSelect.emit(actionFile);
        }
        this.fileAction = undefined;
        this.acceptedFileTypes = undefined;
    }
}
